// Lecture d'une spécification de carte (contrat C0 v1.2, kind « map ») côté interface.
//
// Le backend a validé la spécification : coordonnées réelles uniquement (GPS du véhicule,
// coordonnées d'un lien Google Maps de l'outil, géocodage d'un libellé), jamais de
// position inventée. Ici on la lit de façon tolérante : un point sans coordonnées valides
// est écarté (et compté avec les non localisés), une donnée absente reste absente.
// Fonctions pures, sans React ni MapLibre (testées par node --test).
//
// Forme émise (v1.2, backend/geo/mapVisual.js) : les données de la carte sont dans `map` :
//   {version, kind: "map", visual_type: "markers|heat|route|trace", title, subtitle?, decision,
//    map: {points: [{id, label, lat, lng, geo_source: "lien_maps|donnees|geocodage",
//                    category?, value?, popup?: [{label, value}], maps_url?, precision?,
//                    role?: "depart|etape|arrivee", par_nom?: "point_interet|geo_cloture|vehicule",
//                    signal?: {immatriculation, dernier, age_min, lecture, statut, hors_ligne,
//                              en_mouvement?, vitesse_kmh?}, photo?: {lien, libelle}}],
//          unit, bounds: [[ouest, sud], [est, nord]], focus?: {name, bounds}, cluster,
//          coverage: {total, places, non_localises, details: [{label, raison}]},
//          attributions: [texte], route?: {available, straight_line?, geometry: LineString,
//          distance_km, duration_min, note, reference?: {distance_km, ecart_km, ecart_pct}},
//          trace?: {vehicule {immatriculation, nom}, periode {debut, fin}, positions
//          {enregistrees, renvoyees, pas, tracees, sommets, sans_coordonnees, hors_zone},
//          simplifiee, geometry: LineString, mesures {horodatage[], vitesse_kmh[]}, debut, fin,
//          note}},
//    source, compatible_types, actions}
// v1.10 : `trace` (trajet réellement parcouru, positions GPS lues par le serveur dans les
// données) n'est JAMAIS un itinéraire : ni estimation OSRM, ni distance, ni durée ; elle est
// lue à part de `route`. `signal` : dernier signal du boîtier d'un véhicule (hors ligne : la
// position est la dernière connue).
// v1.11 (type zones) : `zones` = contours des géo-clôtures, lus par le serveur dans les données
// de list_geofences : [{id, label, forme: "polygone|cercle", geometry? (Polygon fermé), sommets?,
// simplifiee?, centre? [lng, lat], rayon_m?, couleur (« #rrggbb » de la plateforme, ou null),
// actif, limite_kmh (null : aucune), portee, vehicule?}], et `geo_clotures` = {outil, total,
// recues, dessinees, inactives, simplifiees, non_dessinees, details [{label, raison}], hors_zone?,
// zone?}. Un cercle (centre et rayon seuls) est dessiné ici en polygone de CIRCLE_SEGMENTS
// segments, pour l'écran comme pour les documents (copie de ce fichier).
// v1.13 : `photo` d'un point = vignette du véhicule (photo extérieure de ses données), lien
// vérifié du même domaine (/api/fichiers/<jeton>) ; tout autre lien est écarté.
// Une forme « à plat » (points, route.waypoints, route.compare.distance_tool_km,
// flagged_straight_line, zones, value_label, popup en objet) reste lue (serveur factice).

import { formatCsvNumber, formatValue } from '../vizFormat.js';
import { MAP_TYPES } from '../visualTypes.js';

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * Créneaux de palette (0 = --ds-viz-1) attribués aux catégories, dans l'ordre : indigo,
 * orange, vert, magenta, lavande, brun, rouge. --ds-viz-3 (bleu foncé, trop proche de
 * --ds-viz-1 en clair) est évité. 7 catégories colorées au plus.
 */
export const CATEGORY_SLOTS = [0, 1, 5, 4, 6, 3, 7];
/** Couleur neutre des catégories regroupées dans « Autres » (--ds-text-3). */
export const OTHER_COLOR = -1;
export const OTHER_CATEGORY = 'Autres';
/** Couleur CSS d'un créneau de catégorie (variable du design system). */
export const categoryColorVar = (slot) => (slot === OTHER_COLOR || slot == null ? 'var(--ds-text-3)' : `var(--ds-viz-${slot + 1})`);
const toNum = (v) => {
  if (isNum(v)) return v;
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v.replace(',', '.')))) return Number(v.replace(',', '.'));
  return null;
};
const str = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v));

const validLat = (v) => isNum(v) && v >= -90 && v <= 90;
const validLng = (v) => isNum(v) && v >= -180 && v <= 180;

/** [lng, lat] valide, ou null (une position 0,0 « île nulle » est refusée : donnée absente). */
export function lngLat(lng, lat) {
  const x = toNum(lng);
  const y = toNum(lat);
  if (!validLng(x) || !validLat(y)) return null;
  if (x === 0 && y === 0) return null;
  return [x, y];
}

// Provenance d'une position : affichée dans l'infobulle (honnêteté sur la précision)
const GEO_SOURCES = [
  [/^(gps|balise|derniere_position|position)/i, 'Position GPS du véhicule'],
  [/(google|maps_link|lien)/i, "Coordonnées du lien Google Maps de l'outil"],
  [/^donn/i, "Coordonnées présentes dans les données de l'outil"],
  [/(nominatim)/i, 'Géocodage du lieu (OpenStreetMap Nominatim, approximatif)'],
  [/(photon|geocod|géocod)/i, 'Géocodage du lieu (approximatif)'],
  [/(outil|tool|mcp)/i, "Coordonnées fournies par l'outil"],
];

export function geoSourceLabel(source) {
  const text = str(source);
  if (!text) return '';
  for (const [re, label] of GEO_SOURCES) if (re.test(text)) return label;
  return text;
}

const PRECISIONS = { adresse: 'adresse', quartier: 'quartier', localite: 'localité', lieu: 'lieu', region: 'région', pays: 'pays' };

// Lieu placé d'après son nom dans les données (v1.10, par_nom)
const BY_NAME = {
  point_interet: "Position du point d'intérêt dans les données de l'outil",
  geo_cloture: "Position de la géo-clôture dans les données de l'outil (centre ou moyenne des sommets)",
  vehicule: "Dernière position connue du véhicule (données de l'outil)",
};

/** Provenance d'un point pour l'infobulle, avec la précision d'un géocodage. */
export function pointSourceText(point) {
  if (point?.parNom && BY_NAME[point.parNom]) return BY_NAME[point.parNom];
  const label = geoSourceLabel(point?.geoSource);
  if (!label) return '';
  const precision = PRECISIONS[point.precision] ?? point.precision;
  return precision ? `${label} · précision : ${precision}` : label;
}

/** Libellé lisible d'un champ technique : « vitesse_max » → « Vitesse max ». */
export function fieldLabel(key) {
  const text = str(key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

const HTTPS_RE = /^https:\/\/[^\s<>"']+$/i;
export const isHttpsUrl = (v) => typeof v === 'string' && HTTPS_RE.test(v.trim());

// Unité devinée d'après le nom du champ (affichage seulement : la valeur reste brute)
function unitFromKey(key) {
  const k = key.toLowerCase();
  if (/(montant|cout|coût|prix|fcfa|amende)/.test(k)) return 'FCFA';
  if (/(vitesse|km_h|kmh)/.test(k)) return 'km/h';
  if (/(distance|_km$|^km)/.test(k)) return 'km';
  if (/(pourcent|taux|_pct$)/.test(k)) return '%';
  return '';
}

/** Hôte lisible d'une URL (« portail.digitrans.ci »), affiché à la place d'un « Ouvrir le lien » opaque. */
export function urlHost(url) {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
}

// Hôtes Google réels (google.com, google.ci, google.co.uk, google.com.br), comme le serveur
const GOOGLE_HOST_RE = /^(?:www\.|maps\.)?google\.(?:com|[a-z]{2}|com?\.[a-z]{2})$/i;
export const isGoogleMapsHost = (url) => {
  try {
    return GOOGLE_HOST_RE.test(new URL(url).hostname);
  } catch {
    return false;
  }
};

// Champs dont la valeur est un identifiant : jamais mise en forme (« 0707123456 », « 2019 »)
const RAW_LABEL_RE = /(t[ée]l[ée]phone|\btel\b|portable|mobile|contact|ann[ée]e|n°|\bno\b|num[ée]ro|\bid\b|identifiant|immat|matricule|code|r[ée]f[ée]rence|\bref\b|\bpv\b|latitude|longitude|\blat\b|\blng\b|\blon\b|ann[ée]e)/i;

/**
 * Champs de l'infobulle : [{label, text, raw, href?}], champs vides omis (MAP-05).
 * Les textes sont affichés TELS QUELS (le serveur les a validés comme texte source :
 * un téléphone garde son 0 initial, une année reste « 2019 ») ; seuls les vrais nombres
 * JSON sont mis en forme, sauf pour un identifiant. `raw` : valeur brute pour le CSV.
 */
export function popupFields(popup) {
  if (!popup || typeof popup !== 'object') return [];
  const entries = Array.isArray(popup)
    ? popup.map((f) => [f?.label ?? f?.key, f?.value, f?.unit])
    : Object.entries(popup).map(([k, v]) => [k, v, null]);
  const out = [];
  for (const [key, raw, unit] of entries) {
    const label = fieldLabel(key);
    if (!label || raw == null || raw === '') continue;
    if (typeof raw === 'boolean') {
      out.push({ label, text: raw ? 'Oui' : 'Non', raw: raw ? 'Oui' : 'Non' });
    } else if (isNum(raw)) {
      const asIs = RAW_LABEL_RE.test(String(key));
      out.push({ label, text: asIs ? String(raw) : formatValue(raw, unit ?? unitFromKey(String(key))), raw: asIs ? String(raw) : formatCsvNumber(raw) });
    } else if (typeof raw === 'string') {
      const text = raw.trim();
      if (!text) continue;
      // Lien sortant (Google Maps de l'outil, fiche) : https uniquement, destination visible
      if (isHttpsUrl(text)) out.push({ label, text: urlHost(text) || text, href: text, raw: text });
      else out.push({ label, text, raw: text });
    } else if (typeof raw === 'object' && !Array.isArray(raw) && raw.value != null) {
      const v = isNum(raw.value) ? raw.value : null;
      out.push({ label, text: v != null ? formatValue(v, str(raw.unit)) : str(raw.value), raw: v != null ? formatCsvNumber(v) : str(raw.value) });
    }
  }
  return out;
}

function normalizePoints(list) {
  const points = [];
  let invalid = 0;
  if (!Array.isArray(list)) return { points, invalid };
  list.forEach((p, index) => {
    if (!p || typeof p !== 'object') return;
    const coords = lngLat(p.lng ?? p.lon ?? p.longitude, p.lat ?? p.latitude);
    if (!coords) {
      invalid += 1;
      return;
    }
    const popup = popupFields(p.popup ?? p.details);
    const mapsUrl = str(p.maps_url);
    if (isHttpsUrl(mapsUrl)) {
      // « Ouvrir dans Google Maps » seulement pour un vrai hôte Google ; sinon l'hôte réel
      popup.push(isGoogleMapsHost(mapsUrl)
        ? { label: 'Google Maps', text: 'Ouvrir dans Google Maps', href: mapsUrl, raw: mapsUrl }
        : { label: 'Lien de carte', text: urlHost(mapsUrl) || mapsUrl, href: mapsUrl, raw: mapsUrl });
    }
    // Vignette du véhicule (v1.13) : champ ajouté seulement quand il existe
    const photo = normalizePhoto(p.photo);
    points.push({
      id: points.length,
      lng: coords[0],
      lat: coords[1],
      label: str(p.label ?? p.name ?? p.titre) || `Point ${index + 1}`,
      category: str(p.category ?? p.categorie ?? p.type),
      value: toNum(p.value ?? p.valeur ?? p.weight),
      popup,
      geoSource: str(p.geo_source ?? p.source),
      precision: str(p.precision),
      role: str(p.role),
      parNom: str(p.par_nom),
      signal: normalizeSignal(p.signal),
      ...(photo ? { photo } : {}),
    });
  });
  return { points, invalid };
}

// Lien vérifié d'une photo (v1.13) : chemin du même domaine signé par le serveur
const PHOTO_LIEN_RE = /^\/api\/fichiers\/f1\.[A-Za-z0-9_-]{16,2800}\.[A-Za-z0-9_-]{43}$/;

/** Vignette du véhicule d'un point (v1.13) : {lien, libelle}, ou null. */
export function normalizePhoto(raw) {
  if (!raw || typeof raw !== 'object' || typeof raw.lien !== 'string' || !PHOTO_LIEN_RE.test(raw.lien)) return null;
  return { lien: raw.lien, libelle: str(raw.libelle).slice(0, 120) || 'Photo du véhicule' };
}

/**
 * Signal du boîtier d'un véhicule (v1.10, get_position_vehicule) : valeurs du serveur telles
 * quelles (heures des données, âge mesuré à la lecture de la plateforme), ou null.
 */
export function normalizeSignal(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const signal = {
    plate: str(raw.immatriculation),
    last: str(raw.dernier),
    ageMin: toNum(raw.age_min),
    readAt: str(raw.lecture),
    status: str(raw.statut),
    offline: raw.hors_ligne === true,
    moving: typeof raw.en_mouvement === 'boolean' ? raw.en_mouvement : null,
    speed: toNum(raw.vitesse_kmh),
  };
  return signal.last || signal.status || signal.offline ? signal : null;
}

/** « 2026-10-12 09:40:00 » ou ISO → « 12/10/2026 09:40 » (heure des données, sans fuseau) ; sinon tel quel. */
export function formatDataDateTime(value) {
  const text = str(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(text);
  if (!m) return text;
  return m[4] ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : `${m[3]}/${m[2]}/${m[1]}`;
}

/** Durée en minutes des données (âge du signal) : « 25 min », « 3 h 10 ». */
export function formatAge(minutes) {
  if (!isNum(minutes) || minutes < 0) return '';
  const total = Math.round(minutes);
  return total < 60 ? `${total} min` : `${Math.floor(total / 60)} h ${String(total % 60).padStart(2, '0')}`;
}

/**
 * Lignes du signal dans l'infobulle et la liste : [{label, text}]. Hors ligne : la position
 * est celle du dernier signal, jamais présentée comme actuelle.
 */
export function signalFields(signal) {
  if (!signal) return [];
  const out = [];
  if (signal.last) {
    const age = formatAge(signal.ageMin);
    const read = signal.readAt ? ` du ${formatDataDateTime(signal.readAt)}` : '';
    out.push({ label: 'Dernier signal', text: `${formatDataDateTime(signal.last)}${age ? ` (${age} avant la mise à jour${read})` : ''}` });
  }
  if (signal.offline) out.push({ label: 'Boîtier', text: 'hors ligne : dernière position connue' });
  else if (signal.status) out.push({ label: 'Boîtier', text: signal.status });
  return out;
}

const ROLE_ORDER = { depart: 0, start: 0, etape: 1, via: 1, arrivee: 2, end: 2 };

function normalizeRoute(raw, points, isRoute) {
  if (!raw || typeof raw !== 'object') {
    if (!isRoute || points.length < 2) return null;
    raw = {};
  }
  const geometry = raw.geometry && typeof raw.geometry === 'object' ? raw.geometry : null;
  const rawCoords = geometry?.type === 'LineString' && Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
  const coordinates = rawCoords
    .map((c) => (Array.isArray(c) ? lngLat(c[0], c[1]) : null))
    .filter(Boolean);
  const waypoints = [];
  let unresolved = 0;
  if (Array.isArray(raw.waypoints)) {
    raw.waypoints.forEach((w) => {
      const coords = w && typeof w === 'object' ? lngLat(w.lng ?? w.lon, w.lat) : null;
      if (!coords) {
        unresolved += 1;
        return;
      }
      waypoints.push({ label: str(w.label ?? w.name), lng: coords[0], lat: coords[1], geoSource: str(w.geo_source), pointId: null });
    });
  } else if (isRoute) {
    // v1.2 : étapes non localisées signalées par le serveur (absentes du tracé)
    const missing = raw.incomplete && Array.isArray(raw.incomplete.etapes_non_localisees) ? raw.incomplete.etapes_non_localisees : [];
    unresolved = missing.length;
    // v1.2 : les étapes sont les points de la carte (départ, étapes, arrivée, dans l'ordre)
    const ordered = points.map((p, i) => ({ p, i })).sort((a, b) => (ROLE_ORDER[a.p.role] ?? 1) - (ROLE_ORDER[b.p.role] ?? 1) || a.i - b.i);
    for (const { p } of ordered) waypoints.push({ label: p.label, lng: p.lng, lat: p.lat, geoSource: p.geoSource, pointId: p.id });
  }
  waypoints.forEach((w, i) => {
    w.role = i === 0 ? 'start' : i === waypoints.length - 1 ? 'end' : 'via';
    // Repère affiché sur la carte : A, 1, 2…, B
    w.mark = w.role === 'start' ? 'A' : w.role === 'end' ? 'B' : String(i);
  });
  // Sans géométrie routière : ligne droite entre les étapes, signalée comme telle
  const straightLine = !!(raw.flagged_straight_line ?? raw.straight_line) || raw.available === false
    || (coordinates.length < 2 && waypoints.length >= 2);
  const line = coordinates.length >= 2 ? coordinates : waypoints.map((w) => [w.lng, w.lat]);
  if (line.length < 2 && !waypoints.length) return null;
  const compare = raw.compare && typeof raw.compare === 'object' ? raw.compare : {};
  const reference = raw.reference && typeof raw.reference === 'object' ? raw.reference : {};
  return {
    coordinates: line,
    distanceKm: toNum(raw.distance_km),
    durationMin: toNum(raw.duration_min),
    waypoints,
    unresolvedWaypoints: unresolved,
    straightLine,
    toolDistanceKm: toNum(compare.distance_tool_km ?? reference.distance_km ?? raw.distance_tool_km),
    // Écart calculé par le serveur (v1.2), sinon recalculé ici
    gapPct: toNum(reference.ecart_pct),
    // Étapes manquantes : la distance estimée ne suit pas le même itinéraire que l'outil
    incomplete: unresolved > 0,
    note: str(raw.note),
    engine: str(raw.engine ?? raw.moteur) || 'OSRM',
  };
}

// ── Zones (v1.11 : contours des géo-clôtures) ──────────────────────────
/** Segments du polygone qui dessine une géo-clôture circulaire (écran, image, documents). */
export const CIRCLE_SEGMENTS = 64;
/** Zones nommées dans une légende (écran, image PNG, documents) ; les suivantes sont comptées. */
export const ZONES_LEGEND_MAX = 8;
const EARTH_RADIUS_M = 6371008.8;
const round6 = (v) => Math.round(v * 1e6) / 1e6;
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const ZONE_SHAPES = { cercle: 'circle', circle: 'circle', polygone: 'polygon', polygon: 'polygon' };

/**
 * Contour fermé [[lng, lat], …] d'un cercle de `radiusM` mètres autour de `center` [lng, lat] :
 * `segments` sommets à égale distance du centre (point de destination sur la sphère), le premier
 * répété à la fin ; [] si le centre ou le rayon est invalide.
 */
export function circleRing(center, radiusM, segments = CIRCLE_SEGMENTS) {
  const c = Array.isArray(center) ? lngLat(center[0], center[1]) : null;
  if (!c || !isNum(radiusM) || radiusM <= 0) return [];
  const [lng0, lat0] = c.map((v) => (v * Math.PI) / 180);
  const d = radiusM / EARTH_RADIUS_M;
  const ring = [];
  for (let k = 0; k < segments; k += 1) {
    const b = (2 * Math.PI * k) / segments;
    const lat = Math.asin(Math.sin(lat0) * Math.cos(d) + Math.cos(lat0) * Math.sin(d) * Math.cos(b));
    const lng = lng0 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(lat0), Math.cos(d) - Math.sin(lat0) * Math.sin(lat));
    ring.push([round6((lng * 180) / Math.PI), round6((lat * 180) / Math.PI)]);
  }
  ring.push(ring[0]);
  return ring;
}

// Ancrage d'une zone (étiquette, infobulle, liste) : centre d'un cercle, sinon moyenne des sommets
// du contour extérieur (premier sommet compté une fois) ; null si le contour est illisible
function zoneAnchor(geometry, center) {
  if (center) return center;
  const ring = geometry.type === 'Polygon' ? geometry.coordinates?.[0] : geometry.coordinates?.[0]?.[0];
  const pts = (Array.isArray(ring) ? ring : []).filter((c) => Array.isArray(c) && validLng(c[0]) && validLat(c[1]));
  const closed = pts.length > 1 && pts[0][0] === pts.at(-1)[0] && pts[0][1] === pts.at(-1)[1];
  const list = closed ? pts.slice(0, -1) : pts;
  if (!list.length) return null;
  return [list.reduce((s, c) => s + c[0], 0) / list.length, list.reduce((s, c) => s + c[1], 0) / list.length];
}

/**
 * Zones de la carte : géo-clôtures du serveur (v1.11 : couleur de la plateforme, état, limite de
 * vitesse, portée ; cercle dessiné en CIRCLE_SEGMENTS segments) ou zones « à plat » (serveur
 * factice : libellé, valeur, géométrie). Zone sans contour lisible écartée.
 */
function normalizeZones(list) {
  if (!Array.isArray(list)) return [];
  const zones = [];
  list.forEach((z, i) => {
    if (!z || typeof z !== 'object') return;
    const shape = ZONE_SHAPES[str(z.forme).toLowerCase()] ?? null;
    const center = shape === 'circle' && Array.isArray(z.centre) ? lngLat(z.centre[0], z.centre[1]) : null;
    const radius = toNum(z.rayon_m);
    let geometry = z.geometry && ['Polygon', 'MultiPolygon'].includes(z.geometry.type) ? z.geometry : null;
    if (!geometry && center && radius > 0) geometry = { type: 'Polygon', coordinates: [circleRing(center, radius)] };
    const anchor = geometry ? zoneAnchor(geometry, center) : null;
    if (!anchor) return;
    const limit = toNum(z.limite_kmh);
    zones.push({
      id: zones.length,
      label: str(z.label ?? z.name) || `Zone ${i + 1}`,
      value: toNum(z.value),
      geometry,
      anchor,
      // Géo-clôture des données (forme et état connus) : infobulle complète, limite « aucune »
      geofence: !!shape || typeof z.actif === 'boolean',
      shape: shape ?? 'polygon',
      radiusM: center && radius > 0 ? radius : null,
      vertices: toNum(z.sommets),
      simplified: z.simplifiee === true,
      color: HEX_COLOR.test(str(z.couleur)) ? str(z.couleur).toLowerCase() : null,
      active: z.actif !== false,
      speedLimit: isNum(limit) && limit > 0 ? limit : null,
      scope: str(z.portee),
      vehicle: str(z.vehicule),
    });
  });
  return zones;
}

/** Décompte des géo-clôtures du serveur (v1.11, `geo_clotures`), ou null (zones « à plat »). */
function normalizeZonesInfo(raw, zones) {
  if (!raw || typeof raw !== 'object') return null;
  const details = Array.isArray(raw.details)
    ? raw.details.map((d) => (d && typeof d === 'object' ? [str(d.label), str(d.raison)].filter(Boolean).join(' : ') : str(d))).filter(Boolean)
    : [];
  return {
    total: toNum(raw.total),
    received: toNum(raw.recues) ?? zones.length,
    drawn: zones.length,
    inactive: zones.filter((z) => !z.active).length,
    simplified: toNum(raw.simplifiees) ?? 0,
    notDrawn: toNum(raw.non_dessinees) ?? 0,
    details,
    outOfZone: toNum(raw.hors_zone) ?? 0,
    zone: str(raw.zone),
  };
}

const SCOPES = { tous_les_vehicules: 'tous les véhicules', vehicule: 'un véhicule' };

/** Forme d'une zone : « Polygone, 4 sommets », « Cercle, rayon 300 m », « Zone » (forme inconnue). */
export function zoneShapeText(zone) {
  if (zone.shape === 'circle') return isNum(zone.radiusM) ? `Cercle, rayon ${formatValue(zone.radiusM, 'm')}` : 'Cercle';
  if (!zone.geofence) return 'Zone';
  return isNum(zone.vertices) ? `Polygone, ${nf(0).format(zone.vertices)} sommet${zone.vertices > 1 ? 's' : ''}` : 'Polygone';
}

/** Limite de vitesse d'une géo-clôture (« 30 km/h »), ou ''. */
export const zoneLimitText = (zone) => (isNum(zone.speedLimit) ? formatValue(zone.speedLimit, 'km/h') : '');

/** Portée d'une géo-clôture : « tous les véhicules », « un véhicule : AB-123-CD ». */
export function zoneScopeText(zone) {
  const scope = SCOPES[zone.scope] ?? zone.scope.replace(/_/g, ' ');
  return zone.vehicle ? `${scope || 'un véhicule'} : ${zone.vehicle}` : scope;
}

/** Entrée de légende d'une zone : son nom, et sa limite de vitesse quand elle en a une. */
export const zoneLegendText = (zone) => (zoneLimitText(zone) ? `${zone.label} · ${zoneLimitText(zone)}` : zone.label);

/** Champs de l'infobulle d'une zone (géo-clôture : forme, limite de vitesse, portée, état). */
export function zoneFields(zone) {
  const out = [{ label: 'Forme', text: zoneShapeText(zone) }];
  if (isNum(zone.value)) out.push({ label: 'Valeur', text: formatValue(zone.value, '') });
  if (!zone.geofence) return out;
  out.push({ label: 'Limite de vitesse', text: zoneLimitText(zone) || 'aucune' });
  const scope = zoneScopeText(zone);
  if (scope) out.push({ label: 'Portée', text: scope });
  out.push({ label: 'État', text: zone.active ? 'active' : 'inactive (tirets gris)' });
  return out;
}

/**
 * Phrase des géo-clôtures : « 5 géo-clôtures dessinées · 1 inactive (tirets gris) » ; en anglais
 * (`lang` « en », image exportée d'une carte anglaise, v1.11) « 5 geofences drawn · 1 inactive
 * (dashed grey) ».
 */
export function zonesCountText(spec, { lang } = {}) {
  const n = nf(0, lang);
  const en = lang === 'en';
  const drawn = spec.zones.length;
  const inactive = spec.zones.filter((z) => !z.active).length;
  const parts = [en
    ? `${n.format(drawn)} geofence${drawn > 1 ? 's' : ''} drawn`
    : `${n.format(drawn)} géo-clôture${drawn > 1 ? 's' : ''} dessinée${drawn > 1 ? 's' : ''}`];
  if (inactive) parts.push(en ? `${n.format(inactive)} inactive (dashed grey)` : `${n.format(inactive)} inactive${inactive > 1 ? 's' : ''} (tirets gris)`);
  return parts.join(' · ');
}

/**
 * Compléments des géo-clôtures, une phrase chacun (résumé, image PNG, résumé accessible) : liste
 * partielle, non dessinées (contour absent ou invalide), hors de la zone demandée, contours
 * simplifiés ; [] sinon. En anglais pour `lang` « en », comme l'image d'un document anglais : les
 * raisons du serveur, écrites en français, n'y sont pas citées.
 */
export function zonesNotes(spec, { lang } = {}) {
  const info = spec.zonesInfo;
  if (!info) return [];
  const n = nf(0, lang);
  const notes = [];
  if (lang === 'en') {
    const geofences = (k) => `${n.format(k)} geofence${k > 1 ? 's' : ''}`;
    if (isNum(info.total) && info.total > info.received) notes.push(`Partial list: ${geofences(info.received)} received out of ${n.format(info.total)}.`);
    if (info.notDrawn > 0) notes.push(`${geofences(info.notDrawn)} not drawn (outline missing or invalid in the data).`);
    if (info.outOfZone > 0) notes.push(`${geofences(info.outOfZone)} outside the ${info.zone ? `zone “${info.zone}”` : 'requested zone'}, not drawn.`);
    if (info.simplified > 0) notes.push(`${n.format(info.simplified)} outline${info.simplified > 1 ? 's' : ''} simplified (actual vertices kept).`);
    return notes;
  }
  if (isNum(info.total) && info.total > info.received) notes.push(`Liste partielle : ${n.format(info.received)} géo-clôtures reçues sur ${n.format(info.total)}.`);
  if (info.notDrawn > 0) {
    const which = info.details.length ? `${info.details.slice(0, 3).join(' ; ')}${info.notDrawn > 3 ? ' ; …' : ''}` : 'contour absent ou invalide dans les données';
    notes.push(`${n.format(info.notDrawn)} géo-clôture${info.notDrawn > 1 ? 's' : ''} non dessinée${info.notDrawn > 1 ? 's' : ''} (${which}).`);
  }
  if (info.outOfZone > 0) {
    notes.push(`${n.format(info.outOfZone)} géo-clôture${info.outOfZone > 1 ? 's' : ''} hors de la zone${info.zone ? ` « ${info.zone} »` : ' demandée'}, non dessinée${info.outOfZone > 1 ? 's' : ''}.`);
  }
  if (info.simplified > 0) notes.push(`${n.format(info.simplified)} contour${info.simplified > 1 ? 's' : ''} simplifié${info.simplified > 1 ? 's' : ''} (sommets réels gardés).`);
  return notes;
}

/** Emprise [[ouest, sud], [est, nord]] d'une zone (recentrage depuis la liste). */
export const zoneBounds = (zone) => computeBounds([], null, [zone]);

/**
 * Style de la pastille d'une zone (légende, liste, infobulle) : la couleur de la géo-clôture sur la
 * plateforme ; inactive ou sans couleur : celle de la feuille de style (Map.css).
 */
export const zoneSwatchStyle = (zone) => (zone.active && zone.color ? { '--zone-color': zone.color } : undefined);

/**
 * Trace GPS (v1.10) : positions réelles reliées dans l'ordre, heure et vitesse de chaque sommet
 * alignées ; cap (`course`, degrés depuis le nord) seulement quand les données en portent
 * (mesures.cap). Sommet sans coordonnées valides écarté avec son heure et sa vitesse. null si
 * moins de 2 sommets. Jamais d'estimation, de distance ni de durée.
 */
export function normalizeTrace(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const geometry = raw.geometry && typeof raw.geometry === 'object' ? raw.geometry : null;
  const coords = geometry?.type === 'LineString' && Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
  const times = Array.isArray(raw.mesures?.horodatage) ? raw.mesures.horodatage : [];
  const speeds = Array.isArray(raw.mesures?.vitesse_kmh) ? raw.mesures.vitesse_kmh : [];
  const courses = Array.isArray(raw.mesures?.cap) ? raw.mesures.cap : null;
  const vertices = [];
  coords.forEach((c, i) => {
    const ll = Array.isArray(c) ? lngLat(c[0], c[1]) : null;
    if (!ll) return;
    const vertex = { id: vertices.length, lng: ll[0], lat: ll[1], time: str(times[i]), speed: toNum(speeds[i]) };
    if (courses) {
      const course = toNum(courses[i]);
      vertex.course = isNum(course) && course >= 0 && course <= 360 ? course % 360 : null;
    }
    vertices.push(vertex);
  });
  if (vertices.length < 2) return null;
  const counts = raw.positions && typeof raw.positions === 'object' ? raw.positions : {};
  const vehicle = raw.vehicule && typeof raw.vehicule === 'object' ? raw.vehicule : {};
  const end = (e, fallback) => {
    const ll = e && typeof e === 'object' ? lngLat(e.lng, e.lat) : null;
    return ll ? { lng: ll[0], lat: ll[1], time: str(e.horodatage), speed: toNum(e.vitesse_kmh) } : fallback;
  };
  return {
    coordinates: vertices.map((v) => [v.lng, v.lat]),
    vertices,
    start: end(raw.debut, vertices[0]),
    end: end(raw.fin, vertices[vertices.length - 1]),
    plate: str(vehicle.immatriculation),
    vehicleName: str(vehicle.nom),
    period: { start: str(raw.periode?.debut), end: str(raw.periode?.fin) },
    counts: {
      recorded: toNum(counts.enregistrees),
      returned: toNum(counts.renvoyees),
      step: toNum(counts.pas),
      drawn: toNum(counts.tracees) ?? vertices.length,
      vertices: toNum(counts.sommets) ?? vertices.length,
      noCoordinates: toNum(counts.sans_coordonnees) ?? 0,
      outOfCountry: toNum(counts.hors_zone) ?? 0,
    },
    simplified: raw.simplifiee === true,
    note: str(raw.note),
  };
}

/** Nom du véhicule d'une trace (immatriculation, sinon nom), ou ''. */
export const traceVehicle = (trace) => trace?.plate || trace?.vehicleName || '';

/**
 * Phrase des positions d'une trace : « 407 positions tracées · 407 renvoyées sur 812
 * enregistrées (une sur 2) » ; en anglais (`lang` « en », image exportée d'une carte anglaise,
 * v1.11) « 407 positions drawn · 407 returned out of 812 recorded (one in 2) ».
 */
export function traceCountsText(trace, { lang } = {}) {
  if (!trace) return '';
  const c = trace.counts;
  const n = nf(0, lang);
  const en = lang === 'en';
  const parts = [en
    ? `${n.format(c.drawn)} position${c.drawn > 1 ? 's' : ''} drawn`
    : `${n.format(c.drawn)} position${c.drawn > 1 ? 's' : ''} tracée${c.drawn > 1 ? 's' : ''}`];
  if (isNum(c.returned) && isNum(c.recorded) && c.recorded > c.returned) {
    const step = isNum(c.step) && c.step > 1 ? n.format(c.step) : '';
    parts.push(en
      ? `${n.format(c.returned)} returned out of ${n.format(c.recorded)} recorded${step ? ` (one in ${step})` : ''}`
      : `${n.format(c.returned)} renvoyées sur ${n.format(c.recorded)} enregistrées${step ? ` (une sur ${step})` : ''}`);
  }
  return parts.join(' · ');
}

/**
 * Période d'une trace d'après ses positions : « du 11/10/2026 06:12 au 11/10/2026 19:43 », en
 * anglais « from 11/10/2026 06:12 to 11/10/2026 19:43 ».
 */
export function tracePeriodText(trace, { lang } = {}) {
  if (!trace?.start?.time || !trace?.end?.time) return '';
  const [start, end] = [formatDataDateTime(trace.start.time), formatDataDateTime(trace.end.time)];
  return lang === 'en' ? `from ${start} to ${end}` : `du ${start} au ${end}`;
}

/**
 * Compléments d'une trace, une phrase chacun (résumé, image PNG, résumé accessible) : positions
 * écartées par le serveur (« Positions non tracées : 5 sans coordonnées, 30 hors du pays
 * couvert. » : un trajet transfrontalier est coupé à la frontière) et tracé simplifié (« Tracé
 * simplifié à 413 sommets, tous des positions réelles. ») ; [] sinon. En anglais pour `lang` « en ».
 */
export function traceNotes(trace, { lang } = {}) {
  if (!trace) return [];
  const c = trace.counts;
  const n = nf(0, lang);
  const en = lang === 'en';
  const notes = [];
  const skipped = [
    c.noCoordinates > 0 ? `${n.format(c.noCoordinates)} ${en ? 'without coordinates' : 'sans coordonnées'}` : '',
    c.outOfCountry > 0 ? `${n.format(c.outOfCountry)} ${en ? 'outside the covered country' : 'hors du pays couvert'}` : '',
  ].filter(Boolean);
  if (skipped.length) notes.push(en ? `Positions not drawn: ${skipped.join(', ')}.` : `Positions non tracées : ${skipped.join(', ')}.`);
  if (trace.simplified) {
    notes.push(en
      ? `Path simplified to ${n.format(c.vertices)} vertices, all actual positions.`
      : `Tracé simplifié à ${n.format(c.vertices)} sommets, tous des positions réelles.`);
  }
  return notes;
}

// ── Liste des positions de la trace (alternative textuelle de la carte) ──
// Trace simplifiée (plus de 600 positions) : la liste, l'infobulle et l'export CSV portent les
// SOMMETS du tracé (des positions réelles, mais pas toutes), jamais présentés comme toutes les
// positions.

/** Titre de la liste : « Positions de la trace (240) », « Sommets de la trace (413 sur 1 150 positions) ». */
export function traceListTitle(trace) {
  const n = nf(0);
  const count = trace.vertices.length;
  return trace.simplified
    ? `Sommets de la trace (${n.format(count)} sur ${n.format(trace.counts.drawn)} positions)`
    : `Positions de la trace (${n.format(count)})`;
}

/**
 * Sommets listés : tous jusqu'à `max`, sinon les `max − 1` premiers et le DERNIER (la fin de la
 * trace, repère B, reste accessible au clavier et au lecteur d'écran).
 */
export function traceListRows(trace, max) {
  const v = trace.vertices;
  return v.length > max ? [...v.slice(0, Math.max(0, max - 1)), v[v.length - 1]] : v;
}

/** Phrase sous une liste incomplète, ou '' : où trouver les sommets non listés. */
export function traceListMore(trace, shown) {
  const total = trace.vertices.length;
  if (shown >= total) return '';
  const n = nf(0);
  return trace.simplified
    ? `${n.format(shown - 1)} premiers sommets et la fin listés sur ${n.format(total)} : les ${n.format(total)} sommets du tracé simplifié sont dans l'export CSV.`
    : `${n.format(shown - 1)} premières positions et la fin listées sur ${n.format(total)} : la liste complète est dans l'export CSV.`;
}

/** Nom d'un sommet de la trace : « Position 5 », ou « Sommet 5 » sur une trace simplifiée. */
export const traceVertexName = (trace, vertex) => `${trace.simplified ? 'Sommet' : 'Position'} ${vertex.id + 1}`;

/** Titre de l'infobulle d'un sommet : début, fin, « Position 5 sur 240 » ou « Sommet 5 sur 413 ». */
export function traceVertexTitle(trace, vertex) {
  const last = trace.vertices.length - 1;
  if (vertex.id === 0) return 'Début de la trace';
  if (vertex.id === last) return 'Fin de la trace';
  return `${traceVertexName(trace, vertex)} sur ${nf(0).format(last + 1)}`;
}

function validBounds(b) {
  if (!Array.isArray(b) || b.length !== 2) return null;
  const sw = Array.isArray(b[0]) ? lngLat(b[0][0], b[0][1]) : null;
  const ne = Array.isArray(b[1]) ? lngLat(b[1][0], b[1][1]) : null;
  if (!sw || !ne || sw[0] > ne[0] || sw[1] > ne[1]) return null;
  return [sw, ne];
}

/** Emprise [[ouest, sud], [est, nord]] des points, du tracé, des zones et de la trace ; null si vide. */
export function computeBounds(points, route, zones = [], trace = null) {
  const coords = [
    ...points.map((p) => [p.lng, p.lat]),
    ...(route?.coordinates ?? []),
    ...(route?.waypoints ?? []).map((w) => [w.lng, w.lat]),
    ...(trace?.coordinates ?? []),
  ];
  for (const z of zones) {
    const polys = z.geometry.type === 'Polygon' ? [z.geometry.coordinates] : z.geometry.coordinates;
    for (const poly of polys ?? []) for (const ring of poly ?? []) for (const c of ring ?? []) if (Array.isArray(c)) coords.push(c);
  }
  const valid = coords.filter((c) => validLng(c[0]) && validLat(c[1]));
  if (!valid.length) return null;
  let [w, s] = valid[0];
  let [e, n] = valid[0];
  for (const [x, y] of valid) {
    w = Math.min(w, x); e = Math.max(e, x);
    s = Math.min(s, y); n = Math.max(n, y);
  }
  // Un seul point : petite emprise autour (≈ 2 km), pour un zoom de quartier
  if (e - w < 0.002 && n - s < 0.002) {
    w -= 0.01; e += 0.01; s -= 0.01; n += 0.01;
  }
  return [[w, s], [e, n]];
}

function normalizeCoverage(raw, located, invalid) {
  const c = raw && typeof raw === 'object' ? raw : {};
  // v1.2 : details [{label, raison}] ; forme à plat : liste de libellés
  const details = Array.isArray(c.details)
    ? c.details.map((d) => (d && typeof d === 'object' ? [str(d.label), str(d.raison ?? d.reason)].filter(Boolean).join(' : ') : str(d))).filter(Boolean)
    : [];
  const list = details.length ? details
    : Array.isArray(c.non_localises) ? c.non_localises.map(str).filter(Boolean)
      : Array.isArray(c.non_localises_labels) ? c.non_localises_labels.map(str).filter(Boolean) : [];
  // Points localisés mais hors de la zone demandée (v1.2 : coverage.hors_zone) : comptés à part
  const isOutOfZone = (text) => /hors de la zone demand/i.test(text);
  const outOfZone = Math.max(0, toNum(c.hors_zone) ?? 0);
  const missingLabels = outOfZone ? list.filter((t) => !isOutOfZone(t)) : list;
  const outOfZoneLabels = outOfZone ? list.filter(isOutOfZone).map((t) => t.replace(/\s*:\s*hors de la zone demand.*$/i, '')) : [];
  const count = toNum(Array.isArray(c.non_localises) ? c.non_localises.length : c.non_localises) ?? missingLabels.length;
  const missing = Math.max(0, count) + invalid;
  const places = toNum(c.places) ?? located;
  const total = toNum(c.total) ?? places + missing + outOfZone;
  return { total, places, missing, missingLabels, outOfZone, outOfZoneLabels, zone: str(c.zone) };
}

/**
 * Spécification de carte normalisée, ou null si rien n'est affichable.
 * @returns {{kind, type, title, subtitle, decision, points, categories, valueLabel, valueUnit,
 *   route, zones, zonesInfo, trace, bounds, coverage, source, actions, geoSources}|null}
 */
export function normalizeMapSpec(spec) {
  if (!spec || typeof spec !== 'object') return null;
  const type = MAP_TYPES.includes(spec.visual_type) ? spec.visual_type : (spec.kind === 'map' ? 'markers' : null);
  if (!type) return null;
  // v1.2 : données dans `map` ; forme à plat sinon
  const m = spec.map && typeof spec.map === 'object' ? spec.map : spec;
  const { points, invalid } = normalizePoints(m.points);
  const route = normalizeRoute(m.route, points, type === 'route');
  const zones = normalizeZones(m.zones);
  const trace = normalizeTrace(m.trace);

  // Catégories dans l'ordre d'apparition. Couleur : créneau de la palette des graphiques
  // dans un ordre qui garde les teintes voisines bien distinctes (CATEGORY_SLOTS) ; au-delà
  // de 7, les suivantes sont regroupées dans « Autres », en couleur neutre (OTHER_COLOR)
  const categories = [];
  const index = new Map();
  let other = null;
  for (const p of points) {
    const name = p.category || '';
    if (!index.has(name)) {
      if (categories.length - (other ? 1 : 0) < CATEGORY_SLOTS.length) {
        index.set(name, categories.length);
        categories.push({ name, color: CATEGORY_SLOTS[categories.length - (other ? 1 : 0)], count: 0 });
      } else {
        if (!other) {
          other = { name: OTHER_CATEGORY, color: OTHER_COLOR, count: 0, members: [] };
          categories.push(other);
        }
        index.set(name, categories.indexOf(other));
        other.members.push(name);
      }
    }
    categories[index.get(name)].count += 1;
  }
  points.forEach((p) => {
    const c = categories[index.get(p.category || '')];
    p.colorIndex = c.color;
    // Filtre de légende : un point regroupé suit la catégorie « Autres »
    p.legendKey = c === other ? OTHER_CATEGORY : (p.category || '');
  });

  const source = spec.source && typeof spec.source === 'object'
    ? {
        toolUseIds: Array.isArray(spec.source.tool_use_ids) ? spec.source.tool_use_ids : [],
        tools: Array.isArray(spec.source.tools) ? spec.source.tools.map(str).filter(Boolean) : [],
        labels: Array.isArray(spec.source.labels) ? spec.source.labels.map(str).filter(Boolean) : [],
        verification: str(spec.source.verification),
        periode: str(spec.source.periode),
        scope: str(spec.source.portee ?? spec.source.scope),
      }
    : null;

  const normalized = {
    kind: 'map',
    type,
    title: str(spec.title),
    subtitle: str(spec.subtitle),
    decision: str(spec.decision),
    points,
    categories,
    valueLabel: str(m.value_label),
    valueUnit: str(m.value_unit ?? m.unit),
    route,
    zones,
    // Géo-clôtures du serveur (v1.11) : reçues, non dessinées, liste partielle
    zonesInfo: normalizeZonesInfo(m.geo_clotures, zones),
    trace,
    bounds: validBounds(m.bounds) ?? computeBounds(points, route, zones, trace),
    focus: m.focus && typeof m.focus === 'object' && str(m.focus.name) ? str(m.focus.name) : '',
    // Regroupement décidé par le serveur (v1.2), sinon par le nombre de points
    cluster: typeof m.cluster === 'boolean' ? m.cluster : null,
    coverage: normalizeCoverage(m.coverage, points.length, invalid),
    attributions: Array.isArray(m.attributions) ? m.attributions.map(str).filter(Boolean) : [],
    source,
    actions: Array.isArray(spec.actions) ? spec.actions : null,
    // Affichages proposés par le serveur (v1.2 : « markers » ↔ « heat », « route » seul)
    compatibleTypes: Array.isArray(spec.compatible_types) ? spec.compatible_types.filter((t) => MAP_TYPES.includes(t)) : null,
    geoSources: [...new Set(points.map((p) => p.geoSource).filter(Boolean))],
    // Points dont le boîtier est hors ligne (marqueur creux, légende)
    offline: points.filter((p) => p.signal?.offline).length,
    // Carte rédigée en anglais (v1.11) : seule son image exportée le suit (mapExport.js) ; l'écran
    // reste en français, et le groupe « Autres » garde ce nom (filtre de la légende)
    ...(spec.lang === 'en' ? { lang: 'en' } : {}),
  };
  // Une carte sans aucun élément localisé reste affichable : état vide explicite (MAP-03)
  return normalized;
}

// Au-delà de ce nombre de points, les marqueurs sont regroupés (MAP-04)
export const CLUSTER_FROM = 12;
/** Points regroupables (décision du serveur, sinon selon leur nombre), quel que soit l'affichage. */
export const clusterable = (spec) => !!spec && spec.points.length > 1 && (spec.cluster ?? spec.points.length >= CLUSTER_FROM);
/** Marqueurs regroupés (groupes chiffrés) ? Jamais sur une carte de densité. */
export const usesClusters = (spec) => !!spec && spec.type === 'markers' && clusterable(spec);

// ── Bascule Repères / Densité (contrat v1.2 : compatible_types) ─────────────
export const MAP_VIEWS = [
  { type: 'markers', label: 'Repères' },
  { type: 'heat', label: 'Densité' },
];
/** Densité : 3 lieux localisés au moins (même seuil que le serveur). */
export const HEAT_MIN_POINTS = 3;

/**
 * Affichages entre lesquels la carte peut basculer, dans l'ordre Repères, Densité ; [] si
 * aucune bascule : itinéraire, liste du serveur absente ou à un seul type, trop peu de lieux.
 */
export function mapViewTypes(spec) {
  if (!spec || spec.route || !Array.isArray(spec.compatibleTypes)) return [];
  if (spec.type !== 'markers' && spec.type !== 'heat') return [];
  const offered = new Set([spec.type, ...spec.compatibleTypes]);
  const views = MAP_VIEWS.map((v) => v.type)
    .filter((t) => offered.has(t) && spec.points.length >= (t === 'heat' ? HEAT_MIN_POINTS : 1));
  return views.length > 1 ? views : [];
}

/**
 * Spécification affichée en `viewType` (null : type d'origine). Une carte basculable garde
 * le même regroupement dans les deux affichages (la source MapLibre ne change pas
 * d'option) : celui du serveur pour des repères, selon le nombre de points pour une carte
 * de densité (le serveur ne regroupe jamais une densité). `views` : affichages proposés.
 */
export function mapViewSpec(spec, viewType = null) {
  const views = mapViewTypes(spec);
  if (!views.length) return spec;
  const type = views.includes(viewType) ? viewType : spec.type;
  const cluster = clusterable({ ...spec, cluster: spec.type === 'markers' ? spec.cluster : null });
  return { ...spec, type, cluster, views };
}

/** La carte a-t-elle quelque chose à tracer ? */
export const hasGeometry = (spec) => !!spec && (spec.points.length > 0 || (spec.route?.coordinates.length ?? 0) >= 2 || spec.zones.length > 0 || (spec.trace?.coordinates.length ?? 0) >= 2);

// ── Formats ────────────────────────────────────────────────────────────
// Français par défaut ; anglais (en-GB) pour l'image exportée d'une carte anglaise (v1.11)
const nf = (max, lang) => new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'fr-FR', { maximumFractionDigits: max });

/** « 342 km », « 8,4 km » ; en anglais « 1,342 km », « 8.4 km ». */
export function formatDistance(km, { lang } = {}) {
  if (!isNum(km)) return '—';
  return `${nf(km < 10 ? 1 : 0, lang).format(km)} km`;
}

/** « 4 h 05 », « 45 min » ; en anglais « 4 h 5 min ». */
export function formatDuration(minutes, { lang } = {}) {
  if (!isNum(minutes)) return '—';
  const total = Math.round(minutes);
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (lang === 'en') return m ? `${h} h ${m} min` : `${h} h`;
  return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`;
}

/** Écart entre la distance estimée et celle de l'outil : {text, pct} ou null. */
export function distanceGap(route) {
  if (!route || route.straightLine || route.incomplete || !isNum(route.distanceKm) || !isNum(route.toolDistanceKm) || route.toolDistanceKm <= 0) return null;
  const pct = isNum(route.gapPct) ? route.gapPct : ((route.distanceKm - route.toolDistanceKm) / route.toolDistanceKm) * 100;
  const text = `${pct > 0 ? '+' : pct < 0 ? '−' : ''}${nf(Math.abs(pct) < 10 ? 1 : 0).format(Math.abs(pct))} %`;
  return { pct, text };
}

/**
 * Phrase de couverture : « 3 lieux non localisés sur 15 … », et à part les lieux
 * localisés mais hors de la zone demandée ; null si tout est placé. En anglais pour `lang` « en ».
 */
export function coverageNote(spec, { lang } = {}) {
  const missing = spec?.coverage?.missing ?? 0;
  const outOfZone = spec?.coverage?.outOfZone ?? 0;
  if (!missing && !outOfZone) return null;
  const total = spec.coverage.total;
  const parts = [];
  if (lang === 'en') {
    const places = (n) => `${n} place${n > 1 ? 's' : ''}`;
    const zone = spec.coverage.zone || spec.focus;
    if (missing) parts.push(`${places(missing)} not located${total > missing ? ` out of ${total}` : ''}: not shown on the map, no position is invented.`);
    if (outOfZone) parts.push(`${places(outOfZone)} outside the ${zone ? `zone “${zone}”` : 'requested zone'}, not shown.`);
    return parts.join(' ');
  }
  if (missing) {
    const plural = missing > 1;
    parts.push(`${missing} lieu${plural ? 'x' : ''} non localisé${plural ? 's' : ''}${total > missing ? ` sur ${total}` : ''} : absent${plural ? 's' : ''} de la carte, aucune position n'est inventée.`);
  }
  if (outOfZone) {
    const plural = outOfZone > 1;
    const zone = spec.coverage.zone || spec.focus;
    parts.push(`${outOfZone} lieu${plural ? 'x' : ''} hors de la zone${zone ? ` « ${zone} »` : ' demandée'}, non affiché${plural ? 's' : ''}.`);
  }
  return parts.join(' ');
}

/** Résumé accessible de la carte (aria-label). */
export function mapAriaSummary(spec) {
  if (!spec) return 'Carte';
  const parts = [`Carte : ${spec.title || 'sans titre'}.`];
  if (spec.points.length) parts.push(`${spec.points.length} lieu${spec.points.length > 1 ? 'x' : ''} localisé${spec.points.length > 1 ? 's' : ''}.`);
  if (spec.route) {
    parts.push(`Itinéraire de ${spec.route.waypoints.length} étape${spec.route.waypoints.length > 1 ? 's' : ''}${isNum(spec.route.distanceKm) ? `, ${formatDistance(spec.route.distanceKm)} estimés` : ''}${spec.route.straightLine ? ', tracé à vol d\'oiseau' : ''}.`);
  }
  if (spec.trace) {
    const vehicle = traceVehicle(spec.trace);
    const period = tracePeriodText(spec.trace);
    parts.push(`Trace GPS réelle${vehicle ? ` de ${vehicle}` : ''} : ${traceCountsText(spec.trace)}${period ? `, ${period}` : ''}, positions reliées en ligne droite, sans distance.`);
    parts.push(...traceNotes(spec.trace));
  }
  if (spec.zones.length) {
    const inactive = spec.zones.filter((z) => !z.active).length;
    const plural = spec.zones.length > 1;
    const names = `${spec.zones.slice(0, 5).map(zoneLegendText).join(', ')}${spec.zones.length > 5 ? ', …' : ''}`;
    parts.push(`${spec.zones.length} zone${plural ? 's' : ''} dessinée${plural ? 's' : ''}${inactive ? `, dont ${inactive} inactive${inactive > 1 ? 's' : ''}` : ''} : ${names}.`);
    parts.push(...zonesNotes(spec));
  }
  if (spec.offline) parts.push(`${spec.offline} boîtier${spec.offline > 1 ? 's' : ''} hors ligne : dernière position connue.`);
  const note = coverageNote(spec);
  if (note) parts.push(note);
  const list = spec.trace && !spec.points.length ? `Liste des ${spec.trace.simplified ? 'sommets' : 'positions'}` : spec.zones.length && !spec.points.length ? 'Liste des zones' : 'Liste des lieux';
  parts.push(`${list} disponible sous la carte.`);
  return parts.join(' ');
}

// ── GeoJSON (calques MapLibre et export) ─────────────────────────────────
/** Points en FeatureCollection (propriétés à plat pour les expressions MapLibre). */
export function pointsGeoJSON(spec, { forExport = false } = {}) {
  const max = Math.max(0, ...spec.points.map((p) => (isNum(p.value) ? Math.abs(p.value) : 0)));
  return {
    type: 'FeatureCollection',
    features: spec.points.map((p) => ({
      type: 'Feature',
      id: p.id,
      geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      properties: forExport
        ? {
            libelle: p.label,
            ...(p.category ? { categorie: p.category } : {}),
            ...(isNum(p.value) ? { valeur: p.value } : {}),
            ...(p.geoSource ? { source_position: p.geoSource } : {}),
            ...(p.signal?.last ? { dernier_signal: p.signal.last } : {}),
            ...(p.signal ? { boitier_hors_ligne: p.signal.offline } : {}),
            ...Object.fromEntries(p.popup.map((f) => [f.label, f.raw ?? f.href ?? f.text])),
          }
        : {
            pid: p.id,
            label: p.label,
            ci: p.colorIndex,
            // Boîtier hors ligne : marqueur creux (dernière position connue)
            off: p.signal?.offline === true,
            // Poids de densité : valeur relative (1 si aucune valeur)
            w: max > 0 && isNum(p.value) ? Math.max(0.05, Math.abs(p.value) / max) : 1,
          },
    })),
  };
}

export function routeGeoJSON(route, coordinates = route?.coordinates ?? []) {
  return {
    type: 'FeatureCollection',
    features: coordinates.length >= 2
      ? [{ type: 'Feature', geometry: { type: 'LineString', coordinates }, properties: {} }]
      : [],
  };
}

export function waypointsGeoJSON(route) {
  return {
    type: 'FeatureCollection',
    features: (route?.waypoints ?? []).map((w, i) => ({
      type: 'Feature',
      id: i,
      geometry: { type: 'Point', coordinates: [w.lng, w.lat] },
      properties: { label: w.label, role: w.role, mark: w.mark },
    })),
  };
}

/** Ligne de la trace GPS (sommets dans l'ordre). */
export function traceGeoJSON(trace) {
  return routeGeoJSON(null, trace?.coordinates ?? []);
}

/** Sommets de la trace (heure et vitesse), cliquables au zoom de rue ; `tid` = rang du sommet. */
export function traceVerticesGeoJSON(trace) {
  return {
    type: 'FeatureCollection',
    features: (trace?.vertices ?? []).map((v) => ({
      type: 'Feature',
      id: v.id,
      geometry: { type: 'Point', coordinates: [v.lng, v.lat] },
      properties: { tid: v.id },
    })),
  };
}

/** Début (A) et fin (B) de la trace, mêmes propriétés que les étapes d'un itinéraire. */
export function traceEndsGeoJSON(trace) {
  if (!trace) return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: [[trace.start, 'start', 'A'], [trace.end, 'end', 'B']].map(([e, role, mark], i) => ({
      type: 'Feature',
      id: i,
      geometry: { type: 'Point', coordinates: [e.lng, e.lat] },
      properties: { role, mark, label: role === 'start' ? 'Début' : 'Fin' },
    })),
  };
}

/**
 * Contours des zones (remplissage et contour, mapLayers.js) : `zid` = rang de la zone, `active`,
 * `color` = couleur de la géo-clôture sur la plateforme (absente : couleur du thème).
 */
export function zonesGeoJSON(zones) {
  return {
    type: 'FeatureCollection',
    features: zones.map((z) => ({
      type: 'Feature',
      id: z.id,
      geometry: z.geometry,
      properties: { zid: z.id, label: z.label, active: z.active, ...(z.color ? { color: z.color } : {}), ...(isNum(z.value) ? { value: z.value } : {}) },
    })),
  };
}

/** Repères et noms des zones, à leur ancrage (centre d'un cercle, moyenne des sommets). */
export function zoneLabelsGeoJSON(zones) {
  return {
    type: 'FeatureCollection',
    features: zones.map((z) => ({
      type: 'Feature',
      id: z.id,
      geometry: { type: 'Point', coordinates: z.anchor },
      properties: { zid: z.id, label: z.label, active: z.active, ...(z.color ? { color: z.color } : {}) },
    })),
  };
}

/**
 * Début d'une ligne jusqu'à la fraction `t` (0-1) de sa longueur (animation progressive
 * du tracé). Distance planaire corrigée de la latitude : suffisant pour l'animation.
 */
export function lineSlice(coordinates, t) {
  if (!Array.isArray(coordinates) || coordinates.length < 2 || t >= 1) return coordinates ?? [];
  if (t <= 0) return coordinates.slice(0, 1);
  const seg = [];
  let total = 0;
  for (let i = 1; i < coordinates.length; i += 1) {
    const [x0, y0] = coordinates[i - 1];
    const [x1, y1] = coordinates[i];
    const k = Math.cos((((y0 + y1) / 2) * Math.PI) / 180);
    const d = Math.hypot((x1 - x0) * k, y1 - y0);
    seg.push(d);
    total += d;
  }
  if (total === 0) return coordinates.slice();
  let target = total * t;
  const out = [coordinates[0]];
  for (let i = 0; i < seg.length; i += 1) {
    if (target >= seg[i]) {
      out.push(coordinates[i + 1]);
      target -= seg[i];
    } else {
      const f = seg[i] ? target / seg[i] : 0;
      const [x0, y0] = coordinates[i];
      const [x1, y1] = coordinates[i + 1];
      out.push([x0 + (x1 - x0) * f, y0 + (y1 - y0) * f]);
      break;
    }
  }
  return out;
}

/** Export GeoJSON complet : points, tracé, étapes, zones (WGS84, [lng, lat]). */
export function exportGeoJSON(spec) {
  const features = [...pointsGeoJSON(spec, { forExport: true }).features];
  if (spec.route) {
    if (spec.route.coordinates.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: spec.route.coordinates },
        properties: {
          type: 'itineraire',
          ...(isNum(spec.route.distanceKm) ? { distance_km_estimee: spec.route.distanceKm } : {}),
          ...(isNum(spec.route.durationMin) ? { duree_min_estimee: spec.route.durationMin } : {}),
          ...(isNum(spec.route.toolDistanceKm) ? { distance_km_outil: spec.route.toolDistanceKm } : {}),
          trace_vol_oiseau: spec.route.straightLine,
        },
      });
    }
    spec.route.waypoints.forEach((w, i) => features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [w.lng, w.lat] },
      properties: { type: 'etape', ordre: i + 1, role: w.role, libelle: w.label },
    }));
  }
  for (const z of spec.zones) {
    // Géo-clôture (v1.11) : contour tel que dessiné (cercle en CIRCLE_SEGMENTS segments), forme,
    // état, limite de vitesse, portée et couleur de la plateforme
    features.push({
      type: 'Feature',
      geometry: z.geometry,
      properties: z.geofence
        ? {
            type: 'geo_cloture',
            libelle: z.label,
            forme: z.shape === 'circle' ? 'cercle' : 'polygone',
            ...(isNum(z.radiusM) ? { rayon_m: z.radiusM, contour: `polygone de ${CIRCLE_SEGMENTS} segments` } : {}),
            ...(isNum(z.vertices) ? { sommets: z.vertices } : {}),
            active: z.active,
            ...(isNum(z.speedLimit) ? { limite_vitesse_kmh: z.speedLimit } : {}),
            ...(z.scope ? { portee: z.scope } : {}),
            ...(z.vehicle ? { vehicule: z.vehicle } : {}),
            ...(z.color ? { couleur: z.color } : {}),
          }
        : { type: 'zone', libelle: z.label, ...(isNum(z.value) ? { valeur: z.value } : {}) },
    });
  }
  if (spec.trace) {
    const t = spec.trace;
    const c = t.counts;
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: t.coordinates },
      properties: {
        type: 'trace_gps',
        ...(t.plate ? { vehicule: t.plate } : t.vehicleName ? { vehicule: t.vehicleName } : {}),
        ...(t.start.time ? { debut: t.start.time } : {}),
        ...(t.end.time ? { fin: t.end.time } : {}),
        positions_tracees: c.drawn,
        ...(isNum(c.returned) ? { positions_renvoyees: c.returned } : {}),
        ...(isNum(c.recorded) ? { positions_enregistrees: c.recorded } : {}),
        ...(isNum(c.step) ? { pas_echantillonnage: c.step } : {}),
        trace_simplifiee: t.simplified,
        // Positions reliées en ligne droite : jamais une distance ni un itinéraire
        nature: 'positions GPS reliées en ligne droite (ni itinéraire ni distance)',
      },
    });
    t.vertices.forEach((v, i) => features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [v.lng, v.lat] },
      properties: { type: 'position_trace', ordre: i + 1, ...(v.time ? { horodatage: v.time } : {}), ...(isNum(v.speed) ? { vitesse_kmh: v.speed } : {}) },
    }));
  }
  return { type: 'FeatureCollection', name: spec.title || 'carte', features };
}

// Colonnes du signal du boîtier (v1.10) : heure du dernier signal telle que dans les données ;
// « hors ligne (dernière position connue) », sinon le statut de la plateforme
const SIGNAL_HEADER = ['Dernier signal', 'Boîtier'];
const signalCsv = (p) => (p.signal ? [p.signal.last, p.signal.offline ? 'hors ligne (dernière position connue)' : p.signal.status] : ['', '']);

/**
 * Lignes CSV d'une trace : Horodatage ; Latitude ; Longitude ; Vitesse (km/h), une ligne par
 * sommet ; lieux ajoutés à la suite (colonne Lieu, signal du boîtier d'un véhicule), sans heure
 * ni vitesse.
 */
function traceCsvRows(spec) {
  const t = spec.trace;
  const withPlaces = spec.points.length > 0;
  const withSignal = spec.points.some((p) => p.signal);
  const header = ['Horodatage', 'Latitude', 'Longitude', 'Vitesse (km/h)', ...(withPlaces ? ['Lieu'] : []), ...(withSignal ? SIGNAL_HEADER : [])];
  const blank = withSignal ? ['', ''] : [];
  const rows = t.vertices.map((v) => [v.time, formatCsvNumber(v.lat), formatCsvNumber(v.lng), isNum(v.speed) ? formatCsvNumber(v.speed) : '', ...(withPlaces ? [''] : []), ...blank]);
  for (const p of spec.points) rows.push(['', formatCsvNumber(p.lat), formatCsvNumber(p.lng), '', p.label, ...(withSignal ? signalCsv(p) : [])]);
  return [header, ...rows];
}

/**
 * Lignes CSV d'une carte des zones (v1.11) : Zone ; Forme ; Limite de vitesse (km/h) ; Portée ;
 * État ; Couleur ; Latitude ; Longitude (ancrage de la zone : centre d'un cercle, moyenne des
 * sommets ; le contour est dans l'export GeoJSON), une ligne par géo-clôture ; lieux ajoutés à la
 * suite (colonne Lieu, signal du boîtier d'un véhicule).
 */
function zonesCsvRows(spec) {
  const withPlaces = spec.points.length > 0;
  const withSignal = spec.points.some((p) => p.signal);
  const header = ['Zone', 'Forme', 'Limite de vitesse (km/h)', 'Portée', 'État', 'Couleur', 'Latitude', 'Longitude', ...(withPlaces ? ['Lieu'] : []), ...(withSignal ? SIGNAL_HEADER : [])];
  const blank = [...(withPlaces ? [''] : []), ...(withSignal ? ['', ''] : [])];
  const rows = spec.zones.map((z) => [
    z.label,
    zoneShapeText(z),
    isNum(z.speedLimit) ? formatCsvNumber(z.speedLimit) : '',
    zoneScopeText(z),
    z.geofence ? (z.active ? 'active' : 'inactive') : '',
    z.color ?? '',
    formatCsvNumber(round6(z.anchor[1])),
    formatCsvNumber(round6(z.anchor[0])),
    ...blank,
  ]);
  for (const p of spec.points) rows.push(['', '', '', '', '', '', formatCsvNumber(p.lat), formatCsvNumber(p.lng), p.label, ...(withSignal ? signalCsv(p) : [])]);
  return [header, ...rows];
}

/** Lignes CSV des points (nombres à virgule décimale, Excel en français) ; trace : ses positions ; zones : les géo-clôtures. */
export function mapCsvRows(spec) {
  if (spec.trace) return traceCsvRows(spec);
  if (spec.type === 'zones' && spec.zones.length) return zonesCsvRows(spec);
  const extra = [];
  for (const p of spec.points) for (const f of p.popup) if (!extra.includes(f.label)) extra.push(f.label);
  const valueHeader = spec.valueLabel ? `${spec.valueLabel}${spec.valueUnit ? ` (${spec.valueUnit})` : ''}` : 'Valeur';
  const hasValue = spec.points.some((p) => isNum(p.value));
  const hasCategory = spec.points.some((p) => p.category);
  // Positions de véhicules (v1.10) : dernier signal et état du boîtier, comme l'infobulle
  const hasSignal = spec.points.some((p) => p.signal);
  const header = ['Lieu', ...(hasCategory ? ['Catégorie'] : []), 'Latitude', 'Longitude', ...(hasValue ? [valueHeader] : []), 'Source de la position', ...(hasSignal ? SIGNAL_HEADER : []), ...extra];
  const roleOf = new Map((spec.route?.waypoints ?? []).filter((w) => w.pointId != null).map((w) => [w.pointId, w]));
  const roleText = (w) => (w.role === 'start' ? 'Départ' : w.role === 'end' ? 'Arrivée' : `Étape ${w.mark}`);
  const rows = spec.points.map((p) => {
    // Valeur brute (jamais la mise en forme de l'infobulle)
    const fields = new Map(p.popup.map((f) => [f.label, f.raw ?? f.href ?? f.text]));
    const w = roleOf.get(p.id);
    return [
      w ? `${roleText(w)} : ${p.label}` : p.label,
      ...(hasCategory ? [p.category] : []),
      formatCsvNumber(p.lat),
      formatCsvNumber(p.lng),
      ...(hasValue ? [formatCsvNumber(p.value)] : []),
      // Lieu placé par son nom (v1.10) : même provenance que l'infobulle (« Dernière position
      // connue du véhicule (données de l'outil) »)
      p.parNom ? pointSourceText(p) : geoSourceLabel(p.geoSource),
      ...(hasSignal ? signalCsv(p) : []),
      ...extra.map((label) => fields.get(label) ?? ''),
    ];
  });
  // Étapes de l'itinéraire à la suite (sans catégorie ni valeur)
  if (spec.route) {
    for (const w of spec.route.waypoints) {
      if (w.pointId != null) continue;
      rows.push([
        `${roleText(w)} : ${w.label}`,
        ...(hasCategory ? ['Itinéraire'] : []),
        formatCsvNumber(w.lat),
        formatCsvNumber(w.lng),
        ...(hasValue ? [''] : []),
        geoSourceLabel(w.geoSource) || 'Géocodage du lieu',
        ...(hasSignal ? ['', ''] : []),
        ...extra.map(() => ''),
      ]);
    }
  }
  return [header, ...rows];
}

/** Vitesse d'une position de trace (« 35 km/h »), ou ''. */
export const speedText = (speed) => (isNum(speed) ? formatValue(speed, 'km/h') : '');

/** Valeur d'un point pour l'affichage (« 112 km/h »), ou ''. */
export function pointValueText(spec, point) {
  return isNum(point.value) ? formatValue(point.value, spec.valueUnit) : '';
}
